(() => {
  const root = document.documentElement

  const syncCheckoutLabel = (root = document) => {
    root.querySelectorAll('#nvd-with-checkout-btn #nvd-subtotal').forEach((label) => {
      const currentLabel = label.textContent.trim()
      const figmaLabel = currentLabel.replace(/\s*\|\s*/g, ' - ')

      if (figmaLabel !== currentLabel) {
        label.textContent = figmaLabel
      }
    })
  }

  let eligibilityTimer
  let eligibilityRequest = 0
  let pendingCartSignature = ''

  const wait = (delay) => new Promise((resolve) => setTimeout(resolve, delay))

  const getMiniCartSignature = () => {
    const drawer = document.querySelector('#cart-drawer')
    if (!drawer) return ''

    const snapshot = drawer.cloneNode(true)
    snapshot.querySelectorAll('.nvd-mini').forEach((node) => node.remove())

    return snapshot.textContent.replace(/\s+/g, ' ').trim()
  }

  const markCartCheckPending = () => {
    pendingCartSignature = getMiniCartSignature()
    root.classList.add('navidium-cart-check-pending')
    root.classList.remove('navidium-minicart-widget-ready')
  }

  let refreshTimer
  let refreshInFlight = false

  const getRefreshTrigger = () => {
    let trigger = document.querySelector('[data-petes-navidium-refresh]')

    if (!trigger && document.body) {
      trigger = document.createElement('button')
      trigger.type = 'button'
      trigger.hidden = true
      trigger.tabIndex = -1
      trigger.setAttribute('aria-hidden', 'true')
      trigger.setAttribute('data-petes-navidium-refresh', '')
      document.body.appendChild(trigger)
    }

    return trigger
  }

  const getMiniCartHost = () =>
    document.querySelector('#cart-drawer .nvd-mini[data-petes-navidium-host]')

  const updateMiniCartReadyState = () => {
    const host = getMiniCartHost()
    const isEligible =
      !root.classList.contains('navidium-cart-check-pending') &&
      !root.classList.contains('navidium-cart-check-failed') &&
      !root.classList.contains('navidium-disabled-for-subscription')
    const hasWidget = Boolean(
      host?.querySelector('#nvd-standard-widget-container') ||
        host?.textContent.trim(),
    )

    root.classList.toggle(
      'navidium-minicart-widget-ready',
      isEligible && hasWidget,
    )
  }

  const triggerNavidiumRefresh = async (allowPending = false) => {
    if (
      refreshInFlight ||
      (!allowPending && root.classList.contains('navidium-cart-check-pending')) ||
      root.classList.contains('navidium-cart-check-failed') ||
      root.classList.contains('navidium-disabled-for-subscription')
    ) {
      return
    }

    const host = getMiniCartHost()
    if (!host) return

    refreshInFlight = true

    try {
      if (typeof nvd_init === 'function') {
        await nvd_init()
      } else {
        getRefreshTrigger()?.dispatchEvent(
          new MouseEvent('click', { bubbles: true }),
        )
      }
    } catch (error) {
      getRefreshTrigger()?.dispatchEvent(
        new MouseEvent('click', { bubbles: true }),
      )
      console.warn('[Navidium] Minicart refresh will retry.', error)
    } finally {
      refreshInFlight = false
      queueSync()
      updateMiniCartReadyState()
    }
  }

  const queueNavidiumRefresh = (delay = 0) => {
    clearTimeout(refreshTimer)
    refreshTimer = setTimeout(triggerNavidiumRefresh, delay)
  }

  const ensureMiniCartHost = () => {
    const checkoutButton = document.querySelector(
      '#cart-drawer button[name="checkout"]',
    )

    if (!checkoutButton) {
      getMiniCartHost()?.remove()
      root.classList.remove('navidium-minicart-widget-ready')
      return null
    }

    const checkoutForm = checkoutButton.closest('form')
    const hostParent = checkoutForm?.parentElement
    if (!checkoutForm || !hostParent) return null

    checkoutForm.classList.add('petes-navidium-native-checkout')

    let host = getMiniCartHost()
    if (!host || host.parentElement !== hostParent) {
      host?.remove()
      host = document.createElement('div')
      host.className = 'nvd-mini w-nvd-100'
      host.setAttribute('data-petes-navidium-host', '')
      hostParent.insertBefore(host, checkoutForm)
      queueNavidiumRefresh()
    }

    return host
  }

  const checkCartEligibility = async () => {
    const requestId = ++eligibilityRequest
    const shopifyRoot = window.Shopify?.routes?.root || '/'

    root.classList.add('navidium-cart-check-pending')

    const initialHost = ensureMiniCartHost()
    const cartLooksLikeSubscription = /deliver every|subscription freebies included/i.test(
      getMiniCartSignature(),
    )
    let widgetWarmup

    if (initialHost && !cartLooksLikeSubscription) {
      initialHost.innerHTML = ''
      widgetWarmup = triggerNavidiumRefresh(true)
    }

    try {
      let cart
      let lastError

      for (let attempt = 0; attempt < 3; attempt += 1) {
        try {
          const response = await fetch(`${shopifyRoot}cart.js`, {
            cache: 'no-store',
            credentials: 'same-origin',
            headers: { Accept: 'application/json' },
          })

          if (!response.ok) {
            throw new Error(`Cart request failed: ${response.status}`)
          }

          cart = await response.json()
          break
        } catch (error) {
          lastError = error
          if (attempt < 2) await wait(300 * 2 ** attempt)
        }
      }

      if (!cart) throw lastError || new Error('Cart request failed')
      if (requestId !== eligibilityRequest) return

      const containsSubscription = cart.items.some(
        (item) => Boolean(item.selling_plan_allocation),
      )

      root.classList.toggle(
        'navidium-disabled-for-subscription',
        containsSubscription,
      )
      root.classList.remove('navidium-cart-check-failed')

      const host = ensureMiniCartHost()
      if (containsSubscription) {
        root.classList.remove('navidium-minicart-widget-ready')
      } else if (host) {
        if (widgetWarmup) await widgetWarmup
        if (!host.textContent.trim()) queueNavidiumRefresh()
      }
    } catch (error) {
      if (requestId !== eligibilityRequest) return

      // Keep paid protection hidden and the native checkout available until a retry succeeds.
      root.classList.add('navidium-cart-check-failed')
      root.classList.remove('navidium-minicart-widget-ready')
      console.warn('[Navidium] Cart eligibility check failed.', error)
    } finally {
      if (requestId === eligibilityRequest) {
        root.classList.remove('navidium-cart-check-pending')
        updateMiniCartReadyState()
      }
    }
  }

  const queueEligibilityCheck = (delay = 150) => {
    clearTimeout(eligibilityTimer)
    eligibilityTimer = setTimeout(checkCartEligibility, delay)
  }

  let syncQueued = false
  const queueSync = () => {
    if (syncQueued) return
    syncQueued = true

    requestAnimationFrame(() => {
      syncQueued = false
      ensureMiniCartHost()
      syncCheckoutLabel()
      updateMiniCartReadyState()
    })
  }

  root.classList.add('navidium-cart-check-pending')

  if (document.readyState === 'loading') {
    document.addEventListener(
      'DOMContentLoaded',
      () => {
        queueSync()
        checkCartEligibility()
      },
      { once: true },
    )
  } else {
    queueSync()
    checkCartEligibility()
  }

  new MutationObserver((mutations) => {
    queueSync()

    if (!root.classList.contains('navidium-cart-check-pending')) return

    const changedOutsideWidget = mutations.some((mutation) => {
      const target =
        mutation.target instanceof Element
          ? mutation.target
          : mutation.target.parentElement

      return Boolean(
        target?.closest('#cart-drawer') && !target.closest('.nvd-mini'),
      )
    })

    if (
      changedOutsideWidget &&
      getMiniCartSignature() !== pendingCartSignature
    ) {
      queueEligibilityCheck(0)
    }
  }).observe(document.documentElement, {
    childList: true,
    subtree: true,
    characterData: true,
  })

  document.addEventListener('toggle-cart', () => queueEligibilityCheck(50))
  document.addEventListener('change', () => queueEligibilityCheck(350), true)
  document.addEventListener(
    'click',
    (event) => {
      const target = event.target instanceof Element ? event.target : null
      const addToCart = target?.closest('button[name="add"]')
      const drawerButton = target?.closest('#cart-drawer button')
      const drawerMutation =
        drawerButton &&
        drawerButton.name !== 'checkout' &&
        drawerButton.textContent.trim() !== 'Close' &&
        !drawerButton.closest('.nvd-mini')

      if (addToCart || drawerMutation) {
        markCartCheckPending()
      }

      // The drawer state-change observer is the fast path; this covers failed or unusual renders.
      queueEligibilityCheck(1200)
    },
    true,
  )
  window.addEventListener('petes:cart-mutated', () => queueEligibilityCheck(0))
  window.addEventListener('pageshow', () => queueEligibilityCheck())
})()
